// src/pages/Officer/CandleDownloader.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Stack, Typography, Button, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select, InputLabel,
  FormControl, Tooltip, alpha, CircularProgress, Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import PreviewIcon from '@mui/icons-material/TableChart';
import TuneIcon from '@mui/icons-material/Tune';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';

// ── TradeWave brand ──
const BRAND = {
  primary: '#00d4ff',
  primaryDeep: '#0066ff',
  gradient: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
  gradientFaint: 'linear-gradient(135deg, rgba(0,212,255,0.14) 0%, rgba(0,102,255,0.08) 100%)',
  bg: '#060a13',
  bgElevated: '#0a1018',
  surface: 'rgba(255,255,255,0.025)',
  surfaceHover: 'rgba(255,255,255,0.04)',
  border: 'rgba(0,212,255,0.12)',
  borderStrong: 'rgba(0,212,255,0.32)',
  text: '#e6edf3',
  textDim: 'rgba(230,237,243,0.6)',
  textMuted: 'rgba(230,237,243,0.35)',
};

const SANS = '"Inter", -apple-system, "Segoe UI", Roboto, sans-serif';
const MONO = '"JetBrains Mono", "Fira Mono", Consolas, monospace';
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const ASSETS    = ['NQ', 'QQQ', 'GC', 'CL', 'ZN', 'ES'];
const SESSIONS  = [{ v: '', l: 'All Sessions' }, { v: 'RTH', l: 'RTH' }, { v: 'ETH', l: 'ETH' }];
const INDICATOR_COLORS = [
  '#00d4ff','#22c55e','#f59e0b','#a855f7','#ef4444',
  '#06b6d4','#84cc16','#f97316','#8b5cf6','#ec4899',
  '#14b8a6','#eab308','#3b82f6','#d946ef','#64748b',
  '#10b981','#fb923c','#818cf8','#f43f5e','#34d399',
];

// Shared input look
const inputSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: SANS, fontSize: '0.85rem', color: BRAND.text,
    bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 1.5,
    '& fieldset': { borderColor: BRAND.border, transition: 'all 0.15s' },
    '&:hover fieldset': { borderColor: BRAND.borderStrong },
    '&.Mui-focused fieldset': { borderColor: BRAND.primary, borderWidth: 1 },
  },
  '& .MuiInputLabel-root': {
    fontFamily: SANS, fontSize: '0.78rem', color: BRAND.textDim,
    '&.Mui-focused': { color: BRAND.primary },
  },
  '& input[type=date]::-webkit-calendar-picker-indicator': {
    filter: 'invert(0.7) brightness(1.2)', cursor: 'pointer',
  },
  '& .MuiSvgIcon-root': { color: BRAND.textMuted },
};

function Pill({ children, active, activeColor = BRAND.primary, onClick, sx = {} }) {
  return (
    <Box onClick={onClick} sx={{
      px: 1.3, py: 0.45, borderRadius: 1, cursor: 'pointer', userSelect: 'none',
      fontSize: '0.68rem', fontFamily: SANS, fontWeight: 600, letterSpacing: '0.04em',
      bgcolor: active ? alpha(activeColor, 0.15) : 'rgba(255,255,255,0.025)',
      color: active ? activeColor : BRAND.textDim,
      border: `1px solid ${active ? alpha(activeColor, 0.45) : 'rgba(255,255,255,0.06)'}`,
      transition: 'all 0.15s',
      '&:hover': { bgcolor: alpha(activeColor, 0.1), color: alpha(activeColor, 0.9), borderColor: alpha(activeColor, 0.3) },
      ...sx,
    }}>{children}</Box>
  );
}

function ParamField({ p, value, onChange }) {
  if (p.type === 'select') return (
    <FormControl size="small" fullWidth sx={inputSx}>
      <InputLabel>{p.label}</InputLabel>
      <Select value={value ?? p.default} onChange={e => onChange(p.key, e.target.value)} label={p.label}>
        {p.options.map(o => (
          <MenuItem key={o} value={o} sx={{ fontSize: '0.85rem', fontFamily: SANS }}>{o}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
  return (
    <TextField size="small" fullWidth label={p.label}
      type={p.type === 'string' ? 'text' : 'number'}
      value={value ?? p.default}
      inputProps={{ min: p.min, max: p.max, step: p.type === 'float' ? 0.1 : 1 }}
      onChange={e => onChange(p.key, p.type === 'string' ? e.target.value : Number(e.target.value))}
      sx={inputSx} />
  );
}

const CAT_COLORS = {
  ma: '#00d4ff', oscillator: '#a855f7', bands: '#f59e0b',
  trend: '#22c55e', volume: '#06b6d4', volatility: '#ef4444',
};

function IndicatorPickerDialog({ open, onClose, meta, categories, onAdd }) {
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useState({});
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');

  useEffect(() => {
    if (open) { setSelected(null); setParams({}); setSearch(''); setActiveCat('all'); }
  }, [open]);

  const handleSelect = (ind) => {
    setSelected(ind);
    const defaults = {};
    ind.params.forEach(p => { defaults[p.key] = p.default; });
    setParams(defaults);
  };

  const handleParam = (key, val) => setParams(p => ({ ...p, [key]: val }));

  const handleAdd = () => {
    if (!selected) return;
    onAdd({ type: selected.type, params: { ...params } });
    onClose();
  };

  const q = search.trim().toLowerCase();
  const filtered = meta.filter(ind => {
    if (activeCat !== 'all' && ind.category !== activeCat) return false;
    if (!q) return true;
    const hay = [ind.type, ind.label, ind.category, ...(ind.keywords || []), ...ind.outputs].join(' ').toLowerCase();
    return hay.includes(q);
  });

  const grouped = {};
  filtered.forEach(ind => {
    const c = ind.category || 'misc';
    (grouped[c] = grouped[c] || []).push(ind);
  });
  const catOrder = ['ma', 'oscillator', 'bands', 'trend', 'volume', 'volatility', 'misc'];
  const cats = catOrder.filter(c => grouped[c]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: {
        bgcolor: BRAND.bgElevated,
        border: `1px solid ${BRAND.border}`,
        borderRadius: 2.5,
        boxShadow: `0 24px 80px rgba(0,0,0,0.5), 0 0 0 1px ${alpha(BRAND.primary, 0.06)}`,
      } }}>
      <DialogTitle sx={{
        fontFamily: SANS, fontSize: '1rem', fontWeight: 700, color: BRAND.text,
        borderBottom: `1px solid rgba(255,255,255,0.05)`,
        py: 1.8, px: 2.8,
        background: BRAND.gradientFaint,
      }}>
        {selected ? (
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <IconButton onClick={() => setSelected(null)} size="small" sx={{
              color: BRAND.textDim, p: 0.6,
              '&:hover': { color: BRAND.primary, bgcolor: alpha(BRAND.primary, 0.08) },
            }}>
              <Typography sx={{ fontSize: '1.1rem', lineHeight: 1, fontFamily: SANS }}>←</Typography>
            </IconButton>
            <Typography component="span" sx={{ fontFamily: MONO, fontWeight: 700, color: BRAND.primary }}>
              {selected.type}
            </Typography>
            <Typography sx={{
              fontSize: '0.82rem', color: BRAND.textDim, fontWeight: 400,
            }}>
              · {selected.label}
            </Typography>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <Box sx={{ width: 3, height: 16, borderRadius: 1, background: BRAND.gradient }} />
            <span>Add Indicator</span>
          </Stack>
        )}
      </DialogTitle>

      {!selected && (
        <Box sx={{ px: 2.8, pt: 1.8, pb: 1.2, borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
          <TextField
            size="small" fullWidth autoFocus placeholder="Search by name, symbol, or keyword…"
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{
              mb: 1.4,
              '& .MuiOutlinedInput-root': {
                fontSize: '0.9rem', fontFamily: SANS, color: BRAND.text,
                bgcolor: alpha('#fff', 0.02), borderRadius: 1.5,
                '& fieldset': { borderColor: BRAND.border },
                '&:hover fieldset': { borderColor: BRAND.borderStrong },
                '&.Mui-focused fieldset': { borderColor: BRAND.primary, borderWidth: 1 },
              },
              '& input::placeholder': { color: BRAND.textMuted, opacity: 1 },
            }}
          />
          <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
            <Pill active={activeCat === 'all'} activeColor={BRAND.primary} onClick={() => setActiveCat('all')}>
              All · {meta.length}
            </Pill>
            {Object.entries(categories || {}).map(([k, label]) => {
              const count = meta.filter(m => m.category === k).length;
              if (!count) return null;
              return (
                <Pill key={k} active={activeCat === k} activeColor={CAT_COLORS[k] || BRAND.primary}
                  onClick={() => setActiveCat(k)}>
                  {label} · {count}
                </Pill>
              );
            })}
          </Stack>
        </Box>
      )}

      <DialogContent sx={{ pt: 2.5, pb: 1.5, px: 2.5, maxHeight: '60vh' }}>
        {!selected ? (
          filtered.length === 0 ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.85rem', fontFamily: SANS, color: BRAND.textDim, fontWeight: 500 }}>
                No indicators match "{search}"
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2.2}>
              {cats.map(catKey => {
                const catLabel = (categories || {})[catKey] || catKey;
                const catColor = CAT_COLORS[catKey] || BRAND.primary;
                return (
                  <Box key={catKey}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <Box sx={{ width: 3, height: 12, borderRadius: 1, bgcolor: catColor }} />
                      <Typography sx={{
                        fontSize: '0.68rem', fontFamily: SANS, fontWeight: 700,
                        letterSpacing: '0.14em', color: catColor, textTransform: 'uppercase',
                      }}>
                        {catLabel}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', fontFamily: MONO, color: BRAND.textMuted }}>
                        {grouped[catKey].length}
                      </Typography>
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 0.9 }}>
                      {grouped[catKey].map(ind => (
                        <Box key={ind.type} onClick={() => handleSelect(ind)} sx={{
                          p: 1.3, borderRadius: 1.8, cursor: 'pointer',
                          bgcolor: alpha(catColor, 0.035),
                          border: `1px solid ${alpha(catColor, 0.14)}`,
                          transition: 'all 0.14s',
                          '&:hover': {
                            bgcolor: alpha(catColor, 0.09),
                            borderColor: alpha(catColor, 0.5),
                            transform: 'translateY(-1px)',
                            boxShadow: `0 6px 18px ${alpha(catColor, 0.2)}`,
                          },
                        }}>
                          <Typography sx={{ fontSize: '0.82rem', fontFamily: MONO, fontWeight: 700, color: catColor }}>
                            {ind.type}
                          </Typography>
                          <Typography sx={{
                            fontSize: '0.72rem', color: BRAND.textDim, mt: 0.3,
                            fontFamily: SANS, fontWeight: 500,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {ind.label}
                          </Typography>
                          <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap sx={{ mt: 0.8 }}>
                            {ind.outputs.slice(0, 3).map(o => (
                              <Chip key={o} label={o} size="small" sx={{
                                height: 15, fontSize: '0.55rem', fontFamily: MONO, fontWeight: 500,
                                bgcolor: alpha(catColor, 0.1), color: alpha(catColor, 0.9),
                                border: `1px solid ${alpha(catColor, 0.18)}`,
                                '& .MuiChip-label': { px: 0.55 },
                              }} />
                            ))}
                            {ind.outputs.length > 3 && (
                              <Chip label={`+${ind.outputs.length - 3}`} size="small" sx={{
                                height: 15, fontSize: '0.55rem', fontFamily: MONO,
                                bgcolor: alpha('#fff', 0.04), color: BRAND.textMuted,
                                '& .MuiChip-label': { px: 0.55 },
                              }} />
                            )}
                          </Stack>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )
        ) : (
          <Box sx={{ pt: 1.5 }}>
            <Stack spacing={1.8}>
              {selected.params.map(p => (
                <ParamField key={p.key} p={p} value={params[p.key]} onChange={handleParam} />
              ))}
            </Stack>
            <Box sx={{
              mt: 1.8, p: 1.2, borderRadius: 1.5,
              bgcolor: alpha(CAT_COLORS[selected.category] || '#22c55e', 0.05),
              border: `1px solid ${alpha(CAT_COLORS[selected.category] || '#22c55e', 0.2)}`,
            }}>
              <Typography sx={{ fontSize: '0.55rem', color: alpha('#fff', 0.4), fontFamily: MONO,
                letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, mb: 0.5 }}>
                Excel columns
              </Typography>
              <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap>
                {selected.outputs.map(o => (
                  <Chip key={o} label={o} size="small" sx={{
                    height: 18, fontSize: '0.55rem', fontFamily: MONO,
                    bgcolor: alpha(CAT_COLORS[selected.category] || '#22c55e', 0.15),
                    color: CAT_COLORS[selected.category] || '#22c55e',
                    '& .MuiChip-label': { px: 0.8 },
                  }} />
                ))}
              </Stack>
            </Box>
          </Box>
        )}
      </DialogContent>
      {selected && (
        <DialogActions sx={{ px: 2.8, pb: 2, pt: 1.2, borderTop: `1px solid rgba(255,255,255,0.05)` }}>
          <Button onClick={onClose}
            sx={{ fontSize: '0.8rem', fontFamily: SANS, fontWeight: 500, color: BRAND.textDim, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleAdd} variant="contained"
            sx={{
              fontSize: '0.82rem', fontFamily: SANS, fontWeight: 600, textTransform: 'none',
              background: BRAND.gradient, color: '#fff', px: 2.8, borderRadius: 1.5, py: 0.8,
              boxShadow: `0 6px 18px ${alpha(BRAND.primary, 0.35)}`,
              '&:hover': { background: BRAND.gradient, boxShadow: `0 8px 24px ${alpha(BRAND.primary, 0.5)}` },
            }}>
            + Add {selected.type}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

function EditIndicatorDialog({ open, indicator, meta, onClose, onSave }) {
  const [params, setParams] = useState({});
  const schema = meta.find(m => m.type === indicator?.type);

  useEffect(() => {
    if (indicator) setParams({ ...indicator.params });
  }, [indicator]);

  if (!schema) return null;

  const handleParam = (key, val) => setParams(p => ({ ...p, [key]: val }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: {
        bgcolor: BRAND.bgElevated,
        border: `1px solid ${BRAND.border}`,
        borderRadius: 2.5,
        boxShadow: `0 20px 60px rgba(0,0,0,0.5)`,
      } }}>
      <DialogTitle sx={{
        fontFamily: SANS, fontSize: '0.95rem', fontWeight: 700, color: BRAND.text,
        borderBottom: `1px solid rgba(255,255,255,0.05)`, py: 1.6, px: 2.5,
      }}>
        Edit Indicator
        <Typography sx={{ fontSize: '0.72rem', color: BRAND.textDim, fontFamily: SANS, fontWeight: 400, mt: 0.2 }}>
          {schema.label} · <span style={{ fontFamily: MONO, color: BRAND.primary }}>{schema.type}</span>
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, pb: 1.5, px: 2.5 }}>
        <Stack spacing={1.6}>
          {schema.params.map(p => (
            <ParamField key={p.key} p={p} value={params[p.key]} onChange={handleParam} />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1, borderTop: `1px solid rgba(255,255,255,0.05)` }}>
        <Button onClick={onClose}
          sx={{ fontSize: '0.8rem', fontFamily: SANS, fontWeight: 500, color: BRAND.textDim, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button onClick={() => { onSave(params); onClose(); }} variant="contained"
          sx={{
            fontSize: '0.8rem', fontFamily: SANS, fontWeight: 600, textTransform: 'none',
            background: BRAND.gradient, color: '#fff', px: 2.5, borderRadius: 1.5,
            boxShadow: `0 4px 14px ${alpha(BRAND.primary, 0.35)}`,
            '&:hover': { background: BRAND.gradient, boxShadow: `0 6px 18px ${alpha(BRAND.primary, 0.5)}` },
          }}>
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function getDefaultDates() {
  const now = new Date();
  const y = now.getFullYear();
  return { start: `${y}-01-01`, end: '' };
}

export default function CandleDownloader() {
  const [meta, setMeta] = useState([]);
  const [categories, setCategories] = useState({});
  const [timeframes, setTimeframes] = useState([]);

  const [asset, setAsset] = useState('NQ');
  const [timeframe, setTimeframe] = useState('1m');
  const [session, setSession] = useState('RTH');
  const { start: defStart, end: defEnd } = getDefaultDates();
  const [startDate, setStartDate] = useState(defStart);
  const [endDate, setEndDate] = useState(defEnd);
  const [indicators, setIndicators] = useState([]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [editIdx, setEditIdx] = useState(null);

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/strategies/api/candles/indicators/`)
      .then(r => r.json())
      .then(d => {
        setMeta(d.indicators || []);
        setCategories(d.categories || {});
        setTimeframes(d.timeframes || []);
      })
      .catch(() => {});
  }, []);

  const buildPayload = useCallback((previewOnly) => ({
    asset, timeframe, session, start_date: startDate, end_date: endDate,
    indicators, preview_only: previewOnly,
  }), [asset, timeframe, session, startDate, endDate, indicators]);

  const handlePreview = async () => {
    setError('');
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await fetch(`${API_BASE}/strategies/api/candles/data/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload(true)),
      });
      const d = await res.json();
      if (!res.ok) { setError(d.error || 'Preview failed'); return; }
      setPreview(d);
    } catch (e) {
      setError(String(e));
    } finally {
      setPreviewing(false);
    }
  };

  const handleDownload = async () => {
    setError('');
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE}/strategies/api/candles/data/csv/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload(false)),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.error || `Download failed (${res.status})`);
        return;
      }
      const blob = await res.blob();
      const sessionTag = session ? `_${session}` : '';
      const filename = `candles_${asset}_${timeframe}${sessionTag}_${startDate}_to_${endDate || 'latest'}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(String(e));
    } finally {
      setDownloading(false);
    }
  };

  const addIndicator = (ind) => {
    if (indicators.length >= 20) return;
    setIndicators(prev => [...prev, ind]);
  };

  const removeIndicator = (i) => setIndicators(prev => prev.filter((_, idx) => idx !== i));

  const saveEdit = (idx, params) => {
    setIndicators(prev => prev.map((ind, i) => i === idx ? { ...ind, params } : ind));
  };

  const editingIndicator = editIdx !== null ? indicators[editIdx] : null;

  const Sel = ({ label, value, onChange, options, sx }) => (
    <FormControl size="small" sx={{ ...inputSx, ...sx }}>
      <InputLabel>{label}</InputLabel>
      <Select value={value} onChange={e => onChange(e.target.value)} label={label}>
        {options.map(o => (
          <MenuItem key={o.v ?? o} value={o.v ?? o} sx={{ fontSize: '0.85rem', fontFamily: SANS }}>
            {o.l ?? o}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{
      minHeight: '100vh',
      background: `radial-gradient(1200px 600px at 10% -10%, ${alpha(BRAND.primary, 0.06)} 0%, transparent 50%), ${BRAND.bg}`,
      color: BRAND.text,
      fontFamily: SANS,
      p: { xs: 2, md: 3 },
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />

      {/* ── Header ── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.8}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 2,
            background: BRAND.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 8px 24px ${alpha(BRAND.primary, 0.35)}`,
          }}>
            <CandlestickChartIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{
              fontFamily: SANS, fontWeight: 700, fontSize: '1.3rem',
              color: BRAND.text, letterSpacing: '-0.02em', lineHeight: 1.1,
            }}>
              Candle Downloader
            </Typography>
            <Typography sx={{
              fontFamily: SANS, fontSize: '0.78rem', color: BRAND.textDim,
              fontWeight: 400, mt: 0.2,
            }}>
              Export OHLC with up to 20 indicators as CSV
            </Typography>
          </Box>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems="flex-start">
        {/* ── LEFT PANEL ── */}
        <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0 }}>
          <Paper elevation={0} sx={{
            background: `linear-gradient(180deg, ${BRAND.surface} 0%, ${alpha('#fff', 0.008)} 100%)`,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 2.5, p: 2.5, mb: 2,
            backdropFilter: 'blur(10px)',
          }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.2 }}>
              <Box sx={{ width: 3, height: 14, borderRadius: 1, background: BRAND.gradient }} />
              <Typography sx={{
                fontFamily: SANS, fontSize: '0.7rem', fontWeight: 700,
                color: BRAND.primary, letterSpacing: '0.14em', textTransform: 'uppercase',
              }}>
                Data Source
              </Typography>
            </Stack>

            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.2}>
                <Sel label="Asset" value={asset} onChange={setAsset} options={ASSETS} sx={{ flex: 1 }} />
                <Sel label="Session" value={session} onChange={setSession} options={SESSIONS} sx={{ flex: 1 }} />
              </Stack>

              <Sel label="Timeframe" value={timeframe} onChange={setTimeframe}
                options={timeframes.length ? timeframes : ['1m']} sx={{ width: '100%' }} />

              <Stack direction="row" spacing={1.2}>
                <TextField size="small" label="Start date" type="date" value={startDate}
                  onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }}
                  sx={{ ...inputSx, flex: 1 }} />
                <TextField size="small" label="End date" type="date" value={endDate}
                  onChange={e => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }}
                  sx={{ ...inputSx, flex: 1 }} />
              </Stack>
            </Stack>
          </Paper>

          {/* Action buttons */}
          <Stack spacing={1.2}>
            <Button fullWidth variant="outlined"
              startIcon={previewing ? <CircularProgress size={14} sx={{ color: BRAND.primary }} /> : <PreviewIcon />}
              onClick={handlePreview} disabled={previewing || downloading}
              sx={{
                fontFamily: SANS, fontSize: '0.85rem', fontWeight: 600, textTransform: 'none',
                py: 1.2, borderRadius: 1.5,
                borderColor: BRAND.borderStrong, color: BRAND.primary,
                bgcolor: alpha(BRAND.primary, 0.04),
                '&:hover': {
                  borderColor: BRAND.primary, bgcolor: alpha(BRAND.primary, 0.08),
                  boxShadow: `0 0 0 3px ${alpha(BRAND.primary, 0.1)}`,
                },
                '&:disabled': { borderColor: alpha('#fff', 0.1), color: BRAND.textMuted },
              }}>
              {previewing ? 'Loading preview…' : 'Preview latest 100 rows'}
            </Button>

            <Button fullWidth variant="contained"
              startIcon={downloading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <DownloadIcon />}
              onClick={handleDownload} disabled={previewing || downloading}
              sx={{
                fontFamily: SANS, fontSize: '0.9rem', fontWeight: 700, textTransform: 'none',
                py: 1.35, borderRadius: 1.5, letterSpacing: '0.01em',
                background: BRAND.gradient, color: '#fff',
                boxShadow: `0 8px 24px ${alpha(BRAND.primary, 0.35)}`,
                '&:hover': {
                  background: BRAND.gradient,
                  boxShadow: `0 10px 32px ${alpha(BRAND.primary, 0.55)}`,
                  transform: 'translateY(-1px)',
                },
                '&:disabled': { background: alpha(BRAND.primary, 0.2), color: alpha('#fff', 0.5), boxShadow: 'none' },
                transition: 'all 0.18s',
              }}>
              {downloading ? 'Building CSV…' : 'Download as CSV'}
            </Button>
          </Stack>

          {error && (
            <Box sx={{
              mt: 1.8, p: 1.4, borderRadius: 1.5,
              bgcolor: alpha('#ef4444', 0.08),
              border: `1px solid ${alpha('#ef4444', 0.25)}`,
            }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#ef4444', fontFamily: SANS, fontWeight: 500 }}>
                {error}
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── RIGHT PANEL ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Indicators card */}
          <Paper elevation={0} sx={{
            background: `linear-gradient(180deg, ${BRAND.surface} 0%, ${alpha('#fff', 0.008)} 100%)`,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 2.5, p: 2.5, mb: 2.5,
            backdropFilter: 'blur(10px)',
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.8 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 3, height: 14, borderRadius: 1, background: BRAND.gradient }} />
                <Typography sx={{
                  fontFamily: SANS, fontSize: '0.7rem', fontWeight: 700,
                  color: BRAND.primary, letterSpacing: '0.14em', textTransform: 'uppercase',
                }}>
                  Indicators
                </Typography>
                <Chip label={`${indicators.length} / 20`} size="small" sx={{
                  height: 20, fontSize: '0.65rem', fontFamily: MONO, fontWeight: 600,
                  bgcolor: indicators.length >= 20 ? alpha('#ef4444', 0.12) : alpha(BRAND.primary, 0.1),
                  color: indicators.length >= 20 ? '#ef4444' : BRAND.primary,
                  border: `1px solid ${indicators.length >= 20 ? alpha('#ef4444', 0.3) : alpha(BRAND.primary, 0.25)}`,
                  '& .MuiChip-label': { px: 0.9 },
                }} />
              </Stack>
              <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={() => setPickerOpen(true)} disabled={indicators.length >= 20}
                sx={{
                  fontFamily: SANS, fontSize: '0.78rem', fontWeight: 600, textTransform: 'none',
                  color: BRAND.primary,
                  border: `1px solid ${BRAND.borderStrong}`,
                  bgcolor: alpha(BRAND.primary, 0.04),
                  borderRadius: 1.5, px: 1.6, py: 0.6,
                  '&:hover': { bgcolor: alpha(BRAND.primary, 0.1), borderColor: BRAND.primary },
                  '&:disabled': { color: BRAND.textMuted, borderColor: alpha('#fff', 0.08), bgcolor: 'transparent' },
                }}>
                Add indicator
              </Button>
            </Stack>

            {indicators.length === 0 ? (
              <Box sx={{
                py: 5, textAlign: 'center',
                border: `1px dashed ${alpha('#fff', 0.08)}`,
                borderRadius: 2,
                background: alpha(BRAND.primary, 0.015),
              }}>
                <Box sx={{
                  width: 44, height: 44, borderRadius: '50%',
                  bgcolor: alpha(BRAND.primary, 0.08),
                  border: `1px solid ${alpha(BRAND.primary, 0.2)}`,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  mb: 1.5,
                }}>
                  <AddIcon sx={{ color: BRAND.primary, fontSize: 22 }} />
                </Box>
                <Typography sx={{ fontSize: '0.88rem', fontFamily: SANS, fontWeight: 600, color: BRAND.text, mb: 0.3 }}>
                  No indicators added yet
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', fontFamily: SANS, color: BRAND.textDim }}>
                  Download will include raw OHLC only — click <strong style={{ color: BRAND.primary }}>Add indicator</strong> to attach up to 20.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {indicators.map((ind, i) => {
                  const color = INDICATOR_COLORS[i % INDICATOR_COLORS.length];
                  const schema = meta.find(m => m.type === ind.type);
                  const paramSummary = schema
                    ? schema.params.filter(p => p.key !== 'label' && p.key !== 'source')
                        .map(p => `${p.key}=${ind.params[p.key] ?? p.default}`)
                        .join(' · ')
                    : '';
                  const customLabel = ind.params?.label;
                  return (
                    <Box key={i} sx={{
                      display: 'flex', alignItems: 'center', gap: 1.4,
                      p: 1.3, borderRadius: 1.8,
                      background: `linear-gradient(90deg, ${alpha(color, 0.06)} 0%, ${alpha(color, 0.02)} 100%)`,
                      border: `1px solid ${alpha(color, 0.2)}`,
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: alpha(color, 0.4), background: alpha(color, 0.08) },
                    }}>
                      <Box sx={{ width: 3, alignSelf: 'stretch', borderRadius: 2, bgcolor: color, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.8}>
                          <Typography sx={{ fontSize: '0.88rem', fontFamily: SANS, fontWeight: 700, color }}>
                            {ind.type}
                          </Typography>
                          <Typography sx={{ fontSize: '0.72rem', fontFamily: SANS, color: BRAND.textDim, fontWeight: 400 }}>
                            {schema?.label}
                          </Typography>
                          {customLabel && (
                            <Chip label={customLabel} size="small" sx={{
                              height: 17, fontSize: '0.62rem', fontFamily: SANS, fontWeight: 600,
                              bgcolor: alpha(color, 0.15), color, border: 'none',
                              '& .MuiChip-label': { px: 0.7 },
                            }} />
                          )}
                        </Stack>
                        {paramSummary && (
                          <Typography sx={{
                            fontSize: '0.7rem', color: BRAND.textMuted,
                            fontFamily: MONO, mt: 0.4,
                          }}>
                            {paramSummary}
                          </Typography>
                        )}
                        {schema && (
                          <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap sx={{ mt: 0.6 }}>
                            {schema.outputs.map(o => (
                              <Chip key={o} label={customLabel ? `${customLabel} ${o}` : o} size="small" sx={{
                                height: 16, fontSize: '0.58rem', fontFamily: MONO, fontWeight: 500,
                                bgcolor: alpha(color, 0.08), color: alpha(color, 0.85),
                                border: `1px solid ${alpha(color, 0.18)}`,
                                '& .MuiChip-label': { px: 0.6 },
                              }} />
                            ))}
                          </Stack>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.3}>
                        <Tooltip title="Edit parameters" arrow>
                          <IconButton size="small" onClick={() => setEditIdx(i)}
                            sx={{
                              color: BRAND.textDim, p: 0.8, borderRadius: 1.2,
                              '&:hover': { color: BRAND.primary, bgcolor: alpha(BRAND.primary, 0.08) },
                            }}>
                            <TuneIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove" arrow>
                          <IconButton size="small" onClick={() => removeIndicator(i)}
                            sx={{
                              color: BRAND.textDim, p: 0.8, borderRadius: 1.2,
                              '&:hover': { color: '#ef4444', bgcolor: alpha('#ef4444', 0.08) },
                            }}>
                            <DeleteIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>

          {/* Preview Table */}
          {preview && (
            <Paper elevation={0} sx={{
              background: `linear-gradient(180deg, ${BRAND.surface} 0%, ${alpha('#fff', 0.008)} 100%)`,
              border: `1px solid ${BRAND.border}`,
              borderRadius: 2.5, overflow: 'hidden',
              backdropFilter: 'blur(10px)',
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{
                px: 2.5, py: 1.5,
                borderBottom: `1px solid ${alpha('#fff', 0.05)}`,
                background: BRAND.gradientFaint,
              }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 3, height: 14, borderRadius: 1, background: BRAND.gradient }} />
                  <Typography sx={{
                    fontFamily: SANS, fontSize: '0.7rem', fontWeight: 700,
                    color: BRAND.primary, letterSpacing: '0.14em', textTransform: 'uppercase',
                  }}>
                    Preview
                  </Typography>
                  <Typography sx={{ fontFamily: SANS, fontSize: '0.78rem', color: BRAND.textDim }}>
                    {preview.total} rows · {preview.columns.length} columns
                  </Typography>
                </Stack>
                <Chip label={`${asset} · ${timeframe}${session ? ' · ' + session : ''}`} size="small" sx={{
                  height: 20, fontSize: '0.7rem', fontFamily: MONO, fontWeight: 600,
                  bgcolor: alpha(BRAND.primary, 0.14), color: BRAND.primary,
                  border: `1px solid ${alpha(BRAND.primary, 0.3)}`,
                  '& .MuiChip-label': { px: 0.9 },
                }} />
              </Stack>
              <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{
                  width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem',
                  fontFamily: MONO, color: BRAND.text,
                }}>
                  <Box component="thead">
                    <Box component="tr" sx={{ bgcolor: alpha('#fff', 0.02) }}>
                      {preview.columns.map(col => (
                        <Box component="th" key={col} sx={{
                          px: 1.5, py: 1, textAlign: 'right', fontWeight: 700,
                          color: BRAND.textDim, letterSpacing: '0.04em',
                          whiteSpace: 'nowrap', fontSize: '0.64rem',
                          fontFamily: SANS, textTransform: 'uppercase',
                          borderBottom: `1px solid ${alpha('#fff', 0.06)}`,
                          '&:first-of-type': { textAlign: 'left' },
                        }}>{col}</Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {preview.rows.map((row, ri) => (
                      <Box component="tr" key={ri} sx={{
                        '&:hover': { bgcolor: alpha(BRAND.primary, 0.03) },
                        borderBottom: `1px solid ${alpha('#fff', 0.03)}`,
                      }}>
                        {preview.columns.map((col, ci) => {
                          const v = row[col];
                          const isTime = col === 'time';
                          const isNull = v === null || v === undefined;
                          return (
                            <Box component="td" key={col} sx={{
                              px: 1.5, py: 0.7, whiteSpace: 'nowrap',
                              textAlign: isTime ? 'left' : 'right',
                              color: isNull ? BRAND.textMuted
                                : isTime ? BRAND.textDim
                                : ci < 5 ? BRAND.text
                                : BRAND.primary,
                            }}>
                              {isNull ? '—' : isTime ? String(v).replace('T', ' ').slice(0, 19) : Number(v).toFixed(4)}
                            </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}
        </Box>
      </Stack>

      <IndicatorPickerDialog open={pickerOpen} onClose={() => setPickerOpen(false)}
        meta={meta} categories={categories} onAdd={addIndicator} />

      <EditIndicatorDialog open={editIdx !== null} indicator={editingIndicator}
        meta={meta} onClose={() => setEditIdx(null)}
        onSave={(params) => { saveEdit(editIdx, params); setEditIdx(null); }} />
    </Box>
  );
}
